import { useCallback, useMemo } from 'react';
import { useApiClient } from 'hooks/useApiClient';
import { API_ROUTES } from 'utils/constants';
import {
  Customer,
  CreateCustomerRequest,
  UpdateCustomerRequest,
} from 'types/customer';

interface PaginatedListResponse<T> {
  data: T[];
  meta: {
    totalPages: number;
  };
}

type PaginatedListClient = {
  get<T>(route: string, params?: Record<string, string>): Promise<T>;
};

async function fetchAllPaginatedCustomers(
  apiClient: PaginatedListClient,
  queryParams: Record<string, string>,
): Promise<Customer[]> {
  const all: Customer[] = [];
  let page = 1;

  while (true) {
    const res = await apiClient.get<PaginatedListResponse<Customer>>(
      API_ROUTES.CUSTOMERS,
      {
        ...queryParams,
        page: String(page),
        limit: '100',
      },
    );
    all.push(...res.data);
    if (page >= res.meta.totalPages) {
      break;
    }
    page += 1;
  }

  return all;
}

/**
 * Customer API service
 * Consolidates all customer-related API calls in a single location
 */
export function useCustomerService() {
  const apiClient = useApiClient();

  /**
   * Get all customers
   * @param includeVoided - Optional flag to include voided customers (default: false)
   */
  const getAllCustomers = useCallback(
    async (includeVoided = false): Promise<Customer[]> => {
      const queryParams: Record<string, string> = {};
      if (includeVoided) {
        queryParams.includeVoided = 'true';
      }
      return fetchAllPaginatedCustomers(apiClient, queryParams);
    },
    [apiClient],
  );

  /**
   * Get a single customer by ID
   */
  const getCustomerById = useCallback(
    async (id: string): Promise<Customer> => {
      return apiClient.get<Customer>(`${API_ROUTES.CUSTOMERS}/${id}`);
    },
    [apiClient],
  );

  /**
   * Create a new customer
   */
  const createCustomer = useCallback(
    async (customer: CreateCustomerRequest): Promise<Customer> => {
      return apiClient.post<Customer>(
        API_ROUTES.CUSTOMERS,
        customer as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Update a customer
   */
  const updateCustomer = useCallback(
    async (id: string, customer: UpdateCustomerRequest): Promise<Customer> => {
      return apiClient.patch<Customer>(
        `${API_ROUTES.CUSTOMERS}/${id}`,
        customer as unknown as Record<string, unknown>,
      );
    },
    [apiClient],
  );

  /**
   * Void a customer
   */
  const voidCustomer = useCallback(
    async (id: string, reason?: string): Promise<Customer> => {
      const queryParams: Record<string, string> = {};
      if (reason) {
        queryParams.reason = reason;
      }
      const result = await apiClient.delete<Customer>(
        `${API_ROUTES.CUSTOMERS}/${id}`,
        undefined,
        queryParams,
      );
      if (!result) {
        throw new Error('Failed to void customer: No response from server');
      }
      return result;
    },
    [apiClient],
  );

  /**
   * Restore a voided customer
   */
  const restoreCustomer = useCallback(
    async (id: string): Promise<Customer> => {
      return apiClient.patch<Customer>(
        `${API_ROUTES.CUSTOMERS}/${id}/restore`,
        {},
      );
    },
    [apiClient],
  );

  /**
   * Get customer history
   */
  const getCustomerHistory = useCallback(
    async (id: string): Promise<Customer[]> => {
      return apiClient.get<Customer[]>(`${API_ROUTES.CUSTOMERS}/${id}/history`);
    },
    [apiClient],
  );

  /**
   * Rollback customer to a snapshot
   */
  const rollbackCustomer = useCallback(
    async (id: string, snapshotId: string): Promise<Customer> => {
      return apiClient.patch<Customer>(
        `${API_ROUTES.CUSTOMERS}/${id}/rollback/${snapshotId}`,
        {},
      );
    },
    [apiClient],
  );

  return useMemo(
    () => ({
      getAllCustomers,
      getCustomerById,
      createCustomer,
      updateCustomer,
      voidCustomer,
      restoreCustomer,
      getCustomerHistory,
      rollbackCustomer,
    }),
    [
      getAllCustomers,
      getCustomerById,
      createCustomer,
      updateCustomer,
      voidCustomer,
      restoreCustomer,
      getCustomerHistory,
      rollbackCustomer,
    ],
  );
}
